import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  LoadingSm,
} from "@medimo/ui";
import { useEffect, useRef, useState } from "react";
import {
  type TwoFactorFormSchemaType,
  useTwoFactorForm,
} from "../../hooks/use-two-factor-form";

interface TwoFactorFormProps {
  isPending: boolean;
  defaultValues: TwoFactorFormSchemaType;
  errorMessage?: string | null;
  onSubmit?: () => void;
  onSuccess?: () => void;
  onGoBack?: () => void;
  onSubmitOverride?: (values: TwoFactorFormSchemaType) => Promise<void>;
}

const getErrorMessage = (message: string): string => {
  if (message.includes("Invalid code received for user")) {
    return "認証コードが無効です。もう一度お試しください。";
  }
  if (message.includes("An error occurred during the sign in process.")) {
    return "サインイン処理中にエラーが発生しました。戻るボタンを押して、再度ログインしてください。";
  }
  if (message.includes("Your software token has already been used once.")) {
    return "この認証コードは既に使用されています。新しい認証コードにて、再度お試しください。";
  }
  if (message.includes("Invalid session for the user, session is expired.")) {
    return "認証コードの入力までに時間がかかりすぎたため、セッションが無効になりました。戻るボタンを押して、再度ログインしてください。";
  }
  if (message.includes("Too many invalid credentials attempts.")) {
    return "認証コードの入力回数が多すぎます。しばらく時間を置いてから再度お試しください。";
  }

  return message;
};

export const TwoFactorForm = ({
  isPending,
  defaultValues,
  errorMessage,
  onSubmit,
  onSuccess,
  onGoBack,
  onSubmitOverride,
}: TwoFactorFormProps) => {
  const { form, handleSubmit, isLoading } = useTwoFactorForm({
    defaultValues,
    onSuccess: onSuccess,
    onMutation: onSubmit,
    onSubmitOverride,
  });

  const [verificationCode, setVerificationCode] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (form.formState.errors.root?.message) {
      const message = form.formState.errors.root.message;
      setFormErrorMessage(getErrorMessage(message));
    } else {
      setFormErrorMessage(errorMessage || null);
    }
  }, [form.formState.errors.root?.message, errorMessage]);

  // 初期フォーカスを設定
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-8">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>認証コード</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="000000"
                  className="text-center font-mono text-lg tracking-widest"
                  autoComplete="one-time-code"
                  spellCheck="false"
                  data-form-type="other"
                  // 数字のみを受け付けるようにonChangeをオーバーライド
                  onChange={(e) => {
                    const slicedValue = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6);
                    setVerificationCode(slicedValue);
                    field.onChange(slicedValue);
                    if (formErrorMessage) {
                      setFormErrorMessage(null);
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {formErrorMessage && (
          <div className="text-destructive">{formErrorMessage}</div>
        )}
        <div className="flex items-center gap-2">
          <Button
            type="submit"
            disabled={isLoading || isPending || verificationCode.length < 6}
          >
            {isLoading || isPending ? <LoadingSm /> : "ログイン"}
          </Button>
          <Button type="button" variant="outline" onClick={onGoBack}>
            戻る
          </Button>
        </div>
      </form>
    </Form>
  );
};
